import { Component, OnInit } from '@angular/core';
import { AngularFireAnalytics } from '@angular/fire/compat/analytics';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { Capacitor } from '@capacitor/core';
import { Keyboard } from '@capacitor/keyboard';
import { StatusBar, Style } from '@capacitor/status-bar';
import { environment } from '@env/outdoorPro/environment';
import { Platform } from '@ionic/angular';
import { Logger } from '@lib/core';
import { filter, map, switchMap } from 'rxjs/operators';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { ToastService } from '@lib/shared/services/toast.service';
import { AppTrackingTransparency } from 'capacitor-plugin-app-tracking-transparency';
import * as Sentry from '@sentry/angular';
import { SplashScreen } from '@capacitor/splash-screen';
const log = new Logger('App');

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false,
})
@Sentry.TraceClass({ name: 'AppComponent' })
export class AppComponent implements OnInit {
  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private titleService: Title,
    private platform: Platform,
    private analytics: AngularFireAnalytics,
    private toastService: ToastService,
    private swUpdate: SwUpdate,
  ) {}

  @Sentry.TraceMethod({ name: 'ngOnInit' })
  async ngOnInit() {
    // Setup logger
    if (environment.production) {
      Logger.enableProductionMode();
    }

    log.debug('init');

    const onNavigationEnd = this.router.events.pipe(filter((event) => event instanceof NavigationEnd));

    // Change page title on navigation, based on route data
    onNavigationEnd
      .pipe(
        map(() => {
          let route = this.activatedRoute;
          while (route.firstChild) {
            route = route.firstChild;
          }
          return route;
        }),
        filter((route) => route.outlet === 'primary'),
        switchMap((route) => route.data),
      )
      .subscribe({
        next: (event) => {
          const title = event.title;
          if (title) {
            this.titleService.setTitle(title);
          }
        },
        error: (error) => {
          console.error(error);
          this.toastService.showDissmissableErrorToast('Error setting page title');
        },
      });

    // Cordova platform and plugins initialization
    await this.platform.ready();
    await this.clearLocalServiceWorkerState();
    this.setupServiceWorkerRecovery();
    this.onCordovaReady();
  }

  private async clearLocalServiceWorkerState(): Promise<void> {
    if (environment.production || !('serviceWorker' in navigator)) {
      return;
    }

    const registrations = await navigator.serviceWorker.getRegistrations();
    await Promise.all(registrations.map((registration) => registration.unregister()));

    if ('caches' in window) {
      const cacheKeys = await caches.keys();
      await Promise.all(cacheKeys.filter((key) => key.includes('ngsw')).map((key) => caches.delete(key)));
    }
  }

  private setupServiceWorkerRecovery(): void {
    if (!this.swUpdate.isEnabled) {
      return;
    }

    this.swUpdate.versionUpdates
      .pipe(filter((event): event is VersionReadyEvent => event.type === 'VERSION_READY'))
      .subscribe(() => {
        window.location.reload();
      });

    this.swUpdate.unrecoverable.subscribe(() => {
      this.toastService.showDissmissableToast(
        'A new version is available. Reloading now.',
        'refresh-outline',
        'primary',
      );
      window.location.reload();
    });
  }

  private async onCordovaReady() {
    log.debug('device ready');

    if (Capacitor.isNativePlatform()) {
      log.debug('Cordova init');

      await Keyboard.setAccessoryBarVisible({ isVisible: true });
      await this.setStatusBarStyleDark();
      await SplashScreen.hide();
      await this.readTrackingPermission();
    }
  }

  private win(res: any) {
    this.analytics.setAnalyticsCollectionEnabled(true);
  }

  private fail(res: any) {
    this.analytics.setAnalyticsCollectionEnabled(false);
  }

  private async askTrackingPermission() {
    if (this.platform.is('ios')) {
      const { status } = await AppTrackingTransparency.requestPermission();
      if (status === 'authorized') {
        this.win(status);
      } else {
        this.fail(status);
      }
    }
  }

  private async readTrackingPermission() {
    if (this.platform.is('ios')) {
      const { status } = await AppTrackingTransparency.getStatus();
      if (status === 'authorized') {
        this.win(status);
      } else if (status === 'denied') {
        this.fail(status);
      } else {
        await this.askTrackingPermission();
      }
    } else {
      this.win('not ios');
    }
  }

  async setStatusBarStyleDark() {
    await StatusBar.setStyle({ style: Style.Dark });
  }
}
